import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import type { AnyAction, Dispatch } from 'redux';
import { HashLoader } from 'react-spinners';
import { applySessionToken } from '../../store/actions/login';
import {
  mfaDispatchApiCall,
  mfaStepUpStartApiCall,
  mfaVerifyApiCall,
} from '../../services/api/users';
import API from '../../services/api/axios';
import {
  flushMfaStepUpQueue,
  rejectMfaStepUpQueue,
  registerMfaStepUpModalOpener,
} from '../../services/mfaStepUpQueue';
import { normalizeMfaChallenge, type MfaChannel, type MfaRequiredPayload } from '../../types/authMfa';
import { APP_ROUTES } from '../../utils/constants/appRoutes';
import { Link as RouterLink } from 'react-router-dom';
import MfaVerifyStep from './MfaVerifyStep';

type Step = 'loading' | 'verify' | 'error';

/**
 * Global modal: when a step-up–protected API returns 401 `MFA step-up required`, the axios layer
 * queues the failed request and opens this flow — `step-up/start` → same MFA verify as login → retry queue.
 */
const MfaStepUpModal: React.FC = () => {
  const dispatch = useDispatch<Dispatch<AnyAction>>();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<Step>('loading');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [mfaSession, setMfaSession] = useState<MfaRequiredPayload | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [dispatching, setDispatching] = useState(false);

  useEffect(() => {
    registerMfaStepUpModalOpener(() => setOpen(true));
  }, []);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setStep('loading');
    setErrorMsg(null);
    setMfaSession(null);

    (async () => {
      try {
        const res = await mfaStepUpStartApiCall();
        const data = res.data;
        if (cancelled) return;
        if ('mfaRequired' in data && data.mfaRequired) {
          setMfaSession({
            mfaContextToken: data.mfaContextToken,
            mfaChallenge: normalizeMfaChallenge(data.mfaChallenge),
          });
          setStep('verify');
          return;
        }
        if ('accessToken' in data && data.accessToken) {
          try {
            applySessionToken(data.accessToken, dispatch);
            await flushMfaStepUpQueue(API);
            setOpen(false);
          } catch {
            setErrorMsg('Failed to store session. Try again.');
            setStep('error');
          }
        }
      } catch (e: unknown) {
        if (cancelled) return;
        const err = e as { response?: { status?: number; data?: { message?: string } } };
        const status = err.response?.status;
        if (status === 400) {
          setErrorMsg(
            err.response?.data?.message ||
              'No MFA methods are configured. Enroll MFA in account security first.'
          );
        } else if (status === 401) {
          setErrorMsg('Your session is no longer valid. Please sign in again.');
        } else {
          setErrorMsg('Could not start verification. Try again.');
        }
        setStep('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [dispatch, open]);

  const handleClose = useCallback(() => {
    rejectMfaStepUpQueue(new Error('MFA step-up cancelled'));
    setOpen(false);
    setMfaSession(null);
    setStep('loading');
    setErrorMsg(null);
  }, []);

  const handleMfaDispatch = useCallback(
    async (channel: 'sms' | 'email') => {
      if (!mfaSession) return;
      setDispatching(true);
      try {
        const res = await mfaDispatchApiCall({
          mfaContextToken: mfaSession.mfaContextToken,
          channel,
        });
        setMfaSession(s =>
          s
            ? {
                ...s,
                mfaChallenge: {
                  ...s.mfaChallenge,
                  ...res.data,
                },
              }
            : null
        );
      } catch (error: unknown) {
        const err = error as { response?: { data?: { message?: string } } };
        setErrorMsg(err.response?.data?.message || 'Could not send verification code.');
        throw error;
      } finally {
        setDispatching(false);
      }
    },
    [mfaSession]
  );

  const handleMfaSubmit = useCallback(
    async (channel: MfaChannel, code: string) => {
      if (!mfaSession) return;
      setVerifying(true);
      setErrorMsg(null);
      try {
        const res = await mfaVerifyApiCall({
          mfaContextToken: mfaSession.mfaContextToken,
          channel,
          code,
        });
        try {
          applySessionToken(res.data.accessToken, dispatch);
        } catch {
          setErrorMsg('Failed to store session. Try again.');
          return;
        }
        await flushMfaStepUpQueue(API);
        setOpen(false);
        setMfaSession(null);
        setStep('loading');
      } catch (e: unknown) {
        const err = e as { response?: { data?: { message?: string } } };
        setErrorMsg(err.response?.data?.message || 'Invalid code. Try again.');
      } finally {
        setVerifying(false);
      }
    },
    [dispatch, mfaSession]
  );

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (step === 'loading') return;
        handleClose();
      }}
      maxWidth={false}
      fullWidth={false}
      disableEscapeKeyDown={step === 'verify' || step === 'loading'}
      PaperProps={{
        sx: {
          width: '100%',
          maxWidth: 420,
          m: 2,
        },
      }}
    >
      <DialogTitle>Verify it&apos;s you</DialogTitle>
      <DialogContent>
        {step === 'loading' && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
            <HashLoader size={40} color="#1b6bae" />
          </div>
        )}
        {step === 'error' && errorMsg && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {errorMsg}
          </Alert>
        )}
        {step === 'verify' && mfaSession && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              This action needs a recent sign-in verification. Enter the code for your chosen method.
            </Typography>
            {errorMsg && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMsg(null)}>
                {errorMsg}
              </Alert>
            )}
            <MfaVerifyStep
              key={mfaSession.mfaContextToken}
              challenge={mfaSession.mfaChallenge}
              loading={verifying}
              dispatchLoading={dispatching}
              onSubmit={handleMfaSubmit}
              onDispatch={handleMfaDispatch}
              onBack={handleClose}
              backButtonLabel="Cancel"
            />
          </>
        )}
      </DialogContent>
      {step === 'error' && (
        <DialogActions>
          <Button component={RouterLink} to={APP_ROUTES.ACCOUNT_SECURITY} onClick={handleClose}>
            Account security
          </Button>
          <Button variant="contained" onClick={handleClose}>
            Close
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default MfaStepUpModal;
